import { useEffect, useRef, useState } from 'react';
import { ErrorProps, TouchedProps } from '../app/passport/RegistrationWrapper/RegistrationForm/field-settings';
import { INPUT_DEBOUNCE_TIME } from '../lib/constants';

/**
 * Builds the map of currently-visible field errors keyed by field name.
 */
const getActiveErrors = (errors: ErrorProps, touched: TouchedProps): Record<string, string> => {
  const active: Record<string, string> = {};
  Object.keys(errors || {}).forEach((key) => {
    if (!touched?.[key]) return;
    const firstInvalid = errors[key]?.find((result) => !result.valid);
    if (firstInvalid?.validationMessage) {
      active[key] = firstInvalid.validationMessage;
    }
  });
  return active;
};

/**
 * Tracks registration-form validation errors and returns the text that should
 * be injected into a polite `aria-live` region so a screen reader announces
 * only the *newly triggered* errors.
 * @param errors  Error map from the registration form's `useValues` state.
 * @param touched Touched map from the registration form's `useValues` state.
 * @returns The string to render inside the live region.
 */
export const useErrorAnnouncer = (errors: ErrorProps, touched: TouchedProps): string => {
  const announcedRef = useRef<Record<string, string>>({});
  const toggleRef = useRef(false);
  const [announcement, setAnnouncement] = useState('');

  const active = getActiveErrors(errors, touched);

  const activeSignature = Object.keys(active)
    .sort()
    .map((key) => `${key}=${active[key]}`)
    .join('|');

  useEffect(() => {
    // Debounce: only announce once the error set has settled.
    const handle = window.setTimeout(() => {
      // Newly triggered = active now but not previously announced (or the message changed).
      const newlyTriggered: string[] = [];
      Object.keys(active).forEach((key) => {
        if (announcedRef.current[key] !== active[key]) {
          newlyTriggered.push(active[key]);
        }
      });

      // Forget fields that are no longer errored so a recurrence re-announces.
      Object.keys(announcedRef.current).forEach((key) => {
        if (!(key in active)) {
          delete announcedRef.current[key];
        }
      });

      // Record the current set of announced errors.
      Object.keys(active).forEach((key) => {
        announcedRef.current[key] = active[key];
      });

      if (newlyTriggered.length) {
        toggleRef.current = !toggleRef.current;
        const suffix = toggleRef.current ? '​' : '';
        setAnnouncement(newlyTriggered.join('. ') + suffix);
      }
    }, INPUT_DEBOUNCE_TIME);

    return () => window.clearTimeout(handle);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeSignature]);

  return announcement;
};

export default useErrorAnnouncer;
